import ErrorMessage from "@/v2-ui/ErrorMessage"
import LicenseFeatureGroupComposeSkeleton from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeSkeleton"
import LicenseFeatureGroupComposition from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposition"
import { ruleSignature } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import { useFeatureGroupFeaturesQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.features.query"
import { useFeatureGroupRulesQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.rules.query"
import type { FeatureGroupFragment } from "@/v2-backoffice-client/license/featureGroup/__types__/license.featureGroup.fragment"

export type LicenseFeatureGroupComposerProps = {
  featureGroup: FeatureGroupFragment
}

// Loads the package as persisted — the codes picked by hand and the content
// rules — and hands both to the composition as drafts. Keyed on both: when
// the stored truth changes under the editor (a save landing, another
// operator's edit arriving through the evict), the drafts reseed from it.
// previousData keeps the editor mounted through the post-save eviction,
// when the cache is briefly empty.
function LicenseFeatureGroupComposer(props: LicenseFeatureGroupComposerProps) {
  const { featureGroup } = props

  const picked = useFeatureGroupFeaturesQuery({
    variables: { featureGroupId: featureGroup.Id, explicitOnly: true },
  })
  const rules = useFeatureGroupRulesQuery({
    variables: { featureGroupId: featureGroup.Id },
  })
  const pickedFeatures = (picked.data ?? picked.previousData)
    ?.getFeatureGroupFeatures
  const ruleRows = (rules.data ?? rules.previousData)?.getFeatureGroupRules

  const error = picked.error ?? rules.error
  if (error) return <ErrorMessage className="text-center" error={error} />
  if (!pickedFeatures || !ruleRows) {
    return <LicenseFeatureGroupComposeSkeleton withViewSwitch />
  }

  return (
    <LicenseFeatureGroupComposition
      key={`${pickedFeatures.map((feature) => feature.Code).join()}#${ruleSignature(ruleRows)}`}
      featureGroup={featureGroup}
      pickedFeatures={pickedFeatures}
      rules={ruleRows}
    />
  )
}

export default LicenseFeatureGroupComposer
